class ReviewsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.autoplay !== 'true') return;

    this.controller = new AbortController();
    this.init(this.controller);
  }

  async init(controller) {
    await customElements.whenDefined('slider-component');

    if (controller.signal.aborted || !this.isConnected) return;

    this.sliderComponent = this.querySelector('slider-component');

    if (!this.sliderComponent?.slider) return;

    this.interval = Math.max(
      2000,
      Number(this.dataset.interval) || 5000,
    );

    this.toggleButton = this.querySelector('[data-autoplay-toggle]');
    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );

    this.manuallyPaused = false;
    this.isHovered = this.matches(':hover');
    this.isFocused = this.isReviewFocused(document.activeElement);

    const options = { signal: controller.signal };

    this.addEventListener(
      'mouseenter',
      () => {
        this.isHovered = true;
        this.schedule();
      },
      options,
    );

    this.addEventListener(
      'mouseleave',
      () => {
        this.isHovered = false;
        this.schedule();
      },
      options,
    );

    this.addEventListener(
      'focusin',
      (event) => {
        this.isFocused = this.isReviewFocused(event.target);
        this.schedule();
      },
      options,
    );

    this.addEventListener(
      'focusout',
      (event) => {
        this.isFocused = this.isReviewFocused(event.relatedTarget);
        this.schedule();
      },
      options,
    );

    document.addEventListener(
      'visibilitychange',
      () => this.schedule(),
      options,
    );

    this.reducedMotion.addEventListener(
      'change',
      () => {
        this.updateToggle();
        this.schedule();
      },
      options,
    );

    this.toggleButton?.addEventListener(
      'click',
      () => {
        this.manuallyPaused = !this.manuallyPaused;
        this.updateToggle();
        this.schedule();
      },
      options,
    );

    // Give each manually selected review its full display time.
    this.sliderComponent.addEventListener(
      'slideChanged',
      () => this.schedule(),
      options,
    );

    this.updateToggle();
    this.schedule();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.timer);
  }

  isReviewFocused(target) {
    return (
      target instanceof Node &&
      this.sliderComponent.contains(target) &&
      !this.toggleButton?.contains(target)
    );
  }

  updateToggle() {
    if (!this.toggleButton) return;

    this.toggleButton.hidden = this.reducedMotion.matches;
    this.toggleButton.textContent = this.manuallyPaused
      ? this.toggleButton.dataset.playLabel
      : this.toggleButton.dataset.pauseLabel;
  }

  schedule() {
    clearTimeout(this.timer);

    if (
      !this.isConnected ||
      this.controller.signal.aborted ||
      this.manuallyPaused ||
      this.isHovered ||
      this.isFocused ||
      document.hidden ||
      this.reducedMotion.matches
    ) {
      return;
    }

    this.timer = setTimeout(() => this.advance(), this.interval);
  }

  advance() {
    const slider = this.sliderComponent;

    if (
      slider.totalPages > 1 &&
      Number.isFinite(slider.sliderItemOffset) &&
      slider.sliderItemOffset > 0
    ) {
      const currentPage = slider.currentPage || 1;
      const nextPage = currentPage >= slider.totalPages
        ? 1
        : currentPage + 1;

      slider.setSlidePosition(
        (nextPage - 1) * slider.sliderItemOffset,
      );
    }

    this.schedule();
  }
}

if (!customElements.get('reviews-carousel')) {
  customElements.define('reviews-carousel', ReviewsCarousel);
}